import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../../components/Header'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import { coursesAPI, getApiBaseUrlWithoutApi } from '../../config/api'
import 'primeicons/primeicons.css'

export default function OnlineCoursePage() {
  const { theme } = useTheme()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [courses, setCourses] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true)
        const data = await coursesAPI.getPublicCourses({ type: 'online' })

        let coursesArray: any[] = []

        if (Array.isArray(data)) {
          coursesArray = data
        } else if (data && Array.isArray(data.results)) {
          coursesArray = data.results
        } else if (data && Array.isArray(data.data)) {
          coursesArray = data.data
        } else if (data && typeof data === 'object') {
          coursesArray = [data]
        }

        const API_BASE_URL = getApiBaseUrlWithoutApi()
        const processedCourses = coursesArray.map((course: any) => {
          let imageUrl = course.image || course.thumbnail || '/images/team-photo-1-BB_Ia2hP.jpg'

          if (imageUrl && imageUrl.startsWith('/media/')) {
            imageUrl = `${API_BASE_URL}${imageUrl}`
          } else if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/images/')) {
            imageUrl = `${API_BASE_URL}${imageUrl}`
          }

          return {
            ...course,
            image: imageUrl
          }
        })

        setCourses(processedCourses)
      } catch (error: any) {
        setCourses([])
      } finally {
        setLoading(false)
      }
    }

    fetchCourses()
  }, [])

  return (
    <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'}`}>
      <Header />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/homepage-background.png"
            alt="Online courses background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10 py-16 md:py-20 px-4 text-center">
          <div className="container mx-auto max-w-4xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gold-400 mb-4">
              Khóa học Online
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto">
            Học mọi lúc, mọi nơi
Nội dung video tinh gọn, dễ áp dụng,
giúp bạn cải thiện toàn diện kỹ năng bóng đá
            </p>
          </div>
        </div>
      </section>

      <section className={`py-12 md:py-16 px-4 ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'}`}>
        <div className="container mx-auto max-w-7xl">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-400 mx-auto mb-4"></div>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Đang tải khóa học...</p>
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-16">
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Chưa có khóa học nào</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {courses.map((course) => {
                // Tính toán giá và discount
                const originalPrice = course.original_price || course.price
                const salePrice = course.sale_price || course.price
                const discountPrice = course.discount_price || course.price
                const currentPrice = salePrice || discountPrice || course.price
                const hasDiscount = originalPrice && currentPrice && originalPrice > currentPrice
                const discountPercent = hasDiscount
                  ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100)
                  : 0
                
                // Kiểm tra is_upcoming (có thể là undefined, null, hoặc false)
                const isUpcoming = course.is_upcoming === true

                return (
                  <div
                    key={course.id}
                    className={`rounded-xl overflow-hidden border ${theme === 'dark' ? 'border-gold-500/40 bg-black/60' : 'border-gold-400 bg-white'} shadow-lg ${theme === 'dark' ? 'shadow-gold-500/20' : 'shadow-gray-200'} hover:shadow-xl transition-all duration-300 group flex flex-col h-full`}
                  >
                    <div className="relative aspect-[16/9] overflow-hidden flex-shrink-0">
                      <img
                        src={course.image || course.thumbnail || '/images/team-photo-1-BB_Ia2hP.jpg'}
                        alt={course.title || course.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />

                      {hasDiscount && discountPercent > 0 && (
                        <div className="absolute top-3 left-3 z-20">
                          <div className="bg-gradient-to-br from-gold-400 via-gold-500 to-gold-600 
                    text-black px-4 py-2 rounded-lg shadow-xl 
                    flex items-center gap-1 border border-black/20">
                            <span className="text-sm font-extrabold leading-none">
                              {discountPercent}
                            </span>
                            <i className="pi pi-percentage text-xs leading-none"></i>
                          </div>
                        </div>
                      )}



                    </div>

                    <div className="p-6 flex flex-col flex-grow">
                      <h3 className={`text-xl md:text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {course.title || course.name}
                      </h3>
                      {course.subtitle && (
                        <p className={`text-sm md:text-base font-semibold mb-3 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                          {course.subtitle}
                        </p>
                      )}

                      <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gold-500/20 flex-shrink-0">
                        {course.video_count && (
                          <div className="flex items-center gap-2">
                            <i className={`pi pi-video text-gold-400`}></i>
                            <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              {course.video_count} video
                            </span>
                          </div>
                        )}
                        {course.duration && (
                          <div className="flex items-center gap-2">
                            <i className={`pi pi-clock text-gold-400`}></i>
                            <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              {course.duration}
                            </span>
                          </div>
                        )}
                        {currentPrice !== undefined && currentPrice !== null && (
                          <div className="flex items-center gap-2">
                            <i className={`pi pi-dollar text-gold-400`}></i>
                            {hasDiscount ? (
                              <div className="flex items-center gap-2">
                                <span className={`text-xs line-through ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {Number(originalPrice).toLocaleString('vi-VN')}đ
                                </span>
                                <span className={`text-sm font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                                  {Number(currentPrice).toLocaleString('vi-VN')}đ
                                </span>
                              </div>
                            ) : (
                              <span className={`text-sm font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                                {Number(currentPrice).toLocaleString('vi-VN')}đ
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (isUpcoming) {
                            // Khóa học sắp ra mắt - không cho phép đăng ký
                            return
                          }
                          if (!isAuthenticated) {
                            // Chưa đăng nhập -> chuyển đến trang thanh toán
                            navigate(`/course/online/payment?courseId=${course.id}`)
                          } else {
                            // Đã đăng nhập -> xem chi tiết
                            navigate(`/course/online/${course.id}`)
                          }
                        }}
                        disabled={isUpcoming}
                        className={`w-full px-6 py-3 font-bold rounded-lg transition flex items-center justify-center gap-2 flex-shrink-0 ${
                          isUpcoming
                            ? 'bg-gold-500/30 border-2 border-gold-500/50 text-gold-400 cursor-not-allowed'
                            : 'bg-gold-gradient text-black hover:shadow-lg hover:shadow-gold-500/50 transform hover:scale-105 active:scale-95'
                        }`}
                      >
                        {isUpcoming ? (
                          <>
                            <i className="pi pi-clock text-gold-400"></i>
                            <span className="text-gold-400">Sắp ra mắt</span>
                          </>
                        ) : (
                          <>
                            <i className={`pi ${!isAuthenticated ? 'pi-shopping-cart' : 'pi-eye'}`}></i>
                            <span>{!isAuthenticated ? 'Đăng ký ngay' : 'Xem chi tiết'}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </section>
    </main>
  )
}

